const FALLBACK_LANGUAGE = "en";

export const normalizeCountryCode = (countryCode) => {
  const code = String(countryCode || "").toUpperCase();
  if (code === "UK") {
    return "GB";
  }
  return code;
};

export const getCountryEmoji = (countryCode) => {
  const code = normalizeCountryCode(countryCode);
  if (!/^[A-Z]{2}$/.test(code)) {
    return "";
  }
  return String.fromCodePoint(
    code.charCodeAt(0) + 127397,
    code.charCodeAt(1) + 127397
  );
};

export const getTranslatedCountryName = (
  countryCode,
  language = FALLBACK_LANGUAGE
) => {
  const code = normalizeCountryCode(countryCode);
  if (!code) {
    return "";
  }
  try {
    const displayNames = new Intl.DisplayNames([language || FALLBACK_LANGUAGE], {
      type: "region",
    });
    return displayNames.of(code) || code;
  } catch {
    return code;
  }
};

export const translateCity = (
  cityValue,
  cityRu,
  language = FALLBACK_LANGUAGE,
) => {
  const city = String(cityValue || "").trim();
  const localizedCity = String(cityRu || "").trim();
  return String(language || FALLBACK_LANGUAGE).toLowerCase().startsWith("ru")
    ? localizedCity || city
    : city;
};

export const sortServersForDisplay = (
  servers,
  { preferredServerName } = {},
) => {
  const preferredName = String(preferredServerName || "").trim();
  const localeCompareOptions = { sensitivity: "base" };

  const getCountrySortKey = (server) =>
    normalizeCountryCode(server?.country_code);
  const getCitySortKey = (server) =>
    String(server?.city || server?.location || "").trim();
  const getServerName = (server) =>
    String(server?.name || server?.hostname || "").trim();

  return [...servers].sort((left, right) => {
    const leftName = getServerName(left);
    const rightName = getServerName(right);

    if (preferredName) {
      const leftPreferred = leftName === preferredName;
      const rightPreferred = rightName === preferredName;
      if (leftPreferred !== rightPreferred) {
        return leftPreferred ? -1 : 1;
      }
    }

    const countryCompare = getCountrySortKey(left).localeCompare(
      getCountrySortKey(right),
      undefined,
      localeCompareOptions,
    );
    if (countryCompare !== 0) {
      return countryCompare;
    }

    const cityCompare = getCitySortKey(left).localeCompare(
      getCitySortKey(right),
      undefined,
      localeCompareOptions,
    );
    if (cityCompare !== 0) {
      return cityCompare;
    }

    return leftName.localeCompare(rightName, undefined, localeCompareOptions);
  });
};

export const formatServerCountryNameLabel = (
  server,
  language = FALLBACK_LANGUAGE
) => {
  const emoji = getCountryEmoji(server?.country_code);
  const country = getTranslatedCountryName(server?.country_code, language);
  const serverName = server?.name || server?.hostname || "";
  const location = [emoji, country].filter(Boolean).join(" ");
  return [location, serverName].filter(Boolean).join(" - ");
};

export const formatServerCityLabel = (
  server,
  language = FALLBACK_LANGUAGE,
) => {
  const emoji = getCountryEmoji(server?.country_code);
  const city = translateCity(
    server?.city || server?.location,
    server?.city_ru,
    language,
  );
  const serverName = server?.name || server?.hostname || "";
  const location =
    emoji && city ? `${emoji}\u00A0\u00A0${city}` : [emoji, city].filter(Boolean).join(" ");
  return [location, serverName].filter(Boolean).join(" - ");
};

export const formatExitTargetLabel = (
  value = {},
  language = FALLBACK_LANGUAGE,
) => {
  const countryCode =
    value.exit_server_country_code || value.last_exit_server_country_code;
  const city = translateCity(
    value.exit_server_city || value.last_exit_server_city,
    value.exit_server_city_ru || value.last_exit_server_city_ru,
    language,
  );
  const serverName = value.exit_server_name || value.last_exit_server_name || "";
  const address = value.exit_address || value.last_exit_address || "";
  const emoji = getCountryEmoji(countryCode);
  const location =
    emoji && city ? `${emoji}\u00A0\u00A0${city}` : [emoji, city].filter(Boolean).join(" ");
  return [location, serverName, address].filter(Boolean).join(" - ");
};
